import { ApolloError, useQuery } from '@apollo/client'
import {
  GET_MARKETS_SPECIFICATION,
  MarketsSpecificationData
} from '../../data/gql/questionnaire/queries/marketsSpecification'
import { LoggerErrorType } from '../../data/logger'
import { captureApolloError } from '../../utils/HelperFunctions'
import { useSurveyId } from '../useSurveyId'

export interface MarketsSpecification {
  /** Map of market (survey) id -> isBase (null/undefined coerced to false). */
  isBaseBySurveyId: Map<string, boolean>
  /** The base market's survey id (the one whose isBase === true), if any. */
  baseSurveyId: string | undefined
  loading: boolean
  error?: ApolloError
  /** Refetch the specification (e.g. after changing the base market). */
  refetch: () => Promise<unknown>
}

/**
 * Reads every market's `isBase` flag for the project that `surveyId` belongs
 * to. `questionnaireAggV2` is a project-level aggregate, so passing any of the
 * project's survey ids returns the specification for all markets, keyed by
 * market (survey) id. Skips while `surveyId` is undefined.
 */
export const useMarketsSpecification = (
  surveyId: string | undefined
): MarketsSpecification => {
  const { data, loading, error, refetch } = useQuery<MarketsSpecificationData>(
    GET_MARKETS_SPECIFICATION,
    {
      context: { clientName: 'questionnaire' },
      variables: { surveyId },
      skip: !surveyId,
      fetchPolicy: 'cache-first',
      onError: (error) => {
        captureApolloError(
          LoggerErrorType.ApolloQuery,
          'marketsSpecification',
          error
        )
      }
    }
  )

  const entries = data?.questionnaireAggV2?.marketsSpecification ?? []

  const isBaseBySurveyId = new Map<string, boolean>(
    entries.map((entry): [string, boolean] => [entry.key, !!entry.value.isBase])
  )
  const baseSurveyId = entries.find((entry) => !!entry.value.isBase)?.key

  return { isBaseBySurveyId, baseSurveyId, loading, error, refetch }
}

/**
 * Whether the current market (route/context surveyId) is the base market, plus
 * whether that determination is still loading. Used to decide whether a global
 * custom audience question is editable here. questionnaireAggV2 returns every
 * market's isBase (see useMarketsSpecification), but the audience card only
 * needs the current market's own flag, so we read just that. Pass
 * `enabled = false` (e.g. outside multi-market projects) to skip the query.
 */
export const useIsBaseMarket = (
  enabled = true
): { isBaseMarket: boolean; loading: boolean } => {
  const surveyId = useSurveyId()
  const { isBaseBySurveyId, loading } = useMarketsSpecification(
    enabled ? surveyId : undefined
  )
  return {
    isBaseMarket: isBaseBySurveyId.get(surveyId) ?? false,
    loading: enabled && loading
  }
}
